/**
 * Ported verbatim (behaviour-for-behaviour) from nazare-frontend's
 * `app/web/app/screens/admin/workflows/format-duration.ts` — same run/step
 * timing math the core app's run-detail view uses, so the two stay
 * indistinguishable to a user comparing them.
 */

type RunTiming = {
  status: string;
  controlState?: "running" | "pause_requested" | "paused";
  pauseAcknowledgedAt?: number | null;
  createdAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
};

/** Stop unfinished child-step timers when their owning run has ended. */
export function runClockMs(run: RunTiming | undefined, now: number): number {
  if (run && ["queued", "running", "sleeping", "paused"].includes(run.status)
    && run.controlState === "paused"
    && typeof run.pauseAcknowledgedAt === "number"
    && Number.isFinite(run.pauseAcknowledgedAt)) {
    return run.pauseAcknowledgedAt;
  }
  if (!run || ["queued", "running", "sleeping", "paused"].includes(run.status)) return now;
  return toMs(run.completedAt) ?? toMs(run.startedAt) ?? toMs(run.createdAt) ?? 0;
}

/** Missing terminal timestamps are unknown durations, not still-running jobs. */
export function runElapsedMs(run: RunTiming, now: number): number | null {
  if (!["queued", "running", "sleeping", "paused"].includes(run.status) && toMs(run.completedAt) === null) return null;
  return elapsedMs(run.startedAt, run.completedAt, runClockMs(run, now));
}

/** Format milliseconds as hours, minutes and seconds, including zero seconds. */
export function formatDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);

  return parts.join(" ");
}

/** Parse an ISO timestamp to epoch-ms, or null when absent/unparseable. */
export function toMs(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : ms;
}

/**
 * Elapsed/wall-clock duration of a run or step in ms, or null when it never
 * started. `endIso` falls back to `now` for in-flight work so the timer ticks.
 */
export function elapsedMs(
  startIso: string | null | undefined,
  endIso: string | null | undefined,
  now: number,
): number | null {
  const start = toMs(startIso);
  if (start === null) return null;
  const end = toMs(endIso) ?? now;
  return Math.max(0, end - start);
}

/** Locale date-time string for an ISO timestamp, or "—" when absent. */
export function formatTimestamp(iso: string | null | undefined): string {
  const ms = toMs(iso);
  return ms === null ? "—" : new Date(ms).toLocaleString();
}

/** ms-aware short duration for the trace axis/bars (sub-second stays visible). */
export function formatTrace(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return formatDuration(ms);
}

/** Wall-clock time (HH:MM:SS) at an absolute epoch-ms instant, for the axis. */
export function formatClock(ms: number): string {
  return new Intl.DateTimeFormat(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(new Date(ms));
}

/** Sentry issue-search deep link for a captured event id (org-wide search, not a direct issue link — matches nazare-frontend's own `config/sentry.ts`). */
const SENTRY_ORG_SLUG = "phoenix-court-group-6o";
export const sentryEventUrl = (eventId: string): string =>
  `https://${SENTRY_ORG_SLUG}.sentry.io/issues/?query=${encodeURIComponent(eventId)}`;
